// Plugin manifest validator. A plugin.json is contributor-authored, so it's the
// boundary where a typo must surface LOUDLY (per CLAUDE.md / docs/UX_PRINCIPLES.md
// "no silent fallbacks") — never a half-registered node. `validateManifest` is a
// pure function returning a structured result; `pluginLoader.loadOne` calls it and
// throws on `!ok`, which the App-level error overlay surfaces.
//
// Hand-rolled rather than ajv/zod — the repo has no JSON-schema dependency, and the
// `errors[]`-array shape mirrors the material-graph compiler. A human/editor-facing
// JSON Schema lives alongside at plugins/_schema/plugin.schema.json (reference only,
// not executed).
//
// See docs/PLUGINS.md for the authored contract these rules enforce.

// Which runtime a node plugs into. `material` is value-emit (GLSL), the rest are
// node-graph contexts. The execution LANE a node runs in is derived from
// graph + phase — see the contract table in docs/PLUGINS.md.
export const GRAPHS = ['body', 'face', 'event', 'material']

// Evaluation phase for body/face nodes. `preMixer` (default) ticks during graph
// evaluation; `postMixer` is RESERVED for rig-driven deformers that need the final
// skinned pose — accepted by the validator now, executed in a later follow-up.
export const PHASES = ['preMixer', 'postMixer']

// Exec pins carry control flow (event graph only). Data pins carry values:
//   - scalar pins (number/boolean/string) flow through the graph as values
//   - anim pins carry a clip-weight contribution (via the node's sentinelClip)
//   - buffer pins carry a retained typed-array view, pulled via instance.readPin
export const PIN_TYPES = ['exec', 'number', 'boolean', 'string', 'anim', 'buffer']
export const DATA_PIN_TYPES = ['number', 'boolean', 'string', 'anim', 'buffer']
export const SCALAR_PIN_TYPES = ['number', 'boolean', 'string']
export const DEFAULTABLE_PIN_TYPES = SCALAR_PIN_TYPES

const HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

// Validate one pin within a node's pin-list. `side` is 'in' | 'out'; `kind` is
// 'exec' | 'data'. Pushes human-readable errors prefixed with `where`.
function validatePin(pin, where, kind, errors, seenHandles) {
  if (!isPlainObject(pin)) {
    errors.push(`${where}: must be an object`)
    return
  }
  if (typeof pin.id !== 'string' || pin.id.length === 0) {
    errors.push(`${where}: missing string "id"`)
    return
  }
  // Handles are addressed by (nodeId, handleId); an input handle and an output
  // handle may share an id (one is a target, one a source), but two handles on
  // the SAME side colliding makes findEdgeByHandle ambiguous.
  if (seenHandles.has(pin.id)) {
    errors.push(`${where}: duplicate pin id "${pin.id}" on the same side`)
  }
  seenHandles.add(pin.id)

  if (kind === 'exec') {
    if (pin.type !== undefined && pin.type !== 'exec') {
      errors.push(`${where} ("${pin.id}"): exec pins must have type "exec" (or omit it)`)
    }
    if (pin.default !== undefined) {
      errors.push(`${where} ("${pin.id}"): exec pins cannot have a "default"`)
    }
    return
  }

  // data pin
  if (typeof pin.type !== 'string' || !DATA_PIN_TYPES.includes(pin.type)) {
    errors.push(`${where} ("${pin.id}"): "type" must be one of ${DATA_PIN_TYPES.join(', ')}`)
  }
  if (pin.default !== undefined && !DEFAULTABLE_PIN_TYPES.includes(pin.type)) {
    errors.push(`${where} ("${pin.id}"): "default" is only valid for ${DEFAULTABLE_PIN_TYPES.join('/')} pins`)
  }
}

function validatePinList(list, listName, where, kind, errors, seenHandles) {
  if (list === undefined) return // absent list is treated as empty
  if (!Array.isArray(list)) {
    errors.push(`${where}.pins.${listName}: must be an array`)
    return
  }
  list.forEach((pin, i) => validatePin(pin, `${where}.pins.${listName}[${i}]`, kind, errors, seenHandles))
}

function validateNode(node, where, errors, seenTypes) {
  if (!isPlainObject(node)) {
    errors.push(`${where}: must be an object`)
    return
  }

  // type — the registry key. Must be namespaced and unique within this manifest
  // (cross-plugin collisions are caught at registration time in pluginNodeRegistry).
  if (typeof node.type !== 'string' || !node.type.startsWith('plugin.')) {
    errors.push(`${where}: "type" must be a string starting with "plugin." (got ${JSON.stringify(node.type)})`)
  } else {
    if (seenTypes.has(node.type)) errors.push(`${where}: duplicate node type "${node.type}" in this manifest`)
    seenTypes.add(node.type)
  }

  const graph = node.graph
  if (!GRAPHS.includes(graph)) {
    errors.push(`${where}: "graph" must be one of ${GRAPHS.join(', ')} (got ${JSON.stringify(graph)})`)
  }
  const isBodyOrFace = graph === 'body' || graph === 'face'

  if (typeof node.label !== 'string' || node.label.length === 0) {
    errors.push(`${where}: missing string "label"`)
  }
  if (node.group !== undefined && typeof node.group !== 'string') {
    errors.push(`${where}: "group" must be a string`)
  }
  if (node.color !== undefined && !HEX_COLOR.test(node.color)) {
    errors.push(`${where}: "color" must be a hex color like #RRGGBB`)
  }

  // perFrame / sentinelClip / phase are body|face concepts — a weight map only
  // exists there. Flagging misuse catches a contributor who, e.g., puts perFrame
  // on an event node where it would silently never run.
  if (node.perFrame !== undefined && typeof node.perFrame !== 'boolean') {
    errors.push(`${where}: "perFrame" must be a boolean`)
  }
  // `container`: the node owns an inner graph (data.innerNodes/innerEdges), the shape a
  // state machine and a graphGroup already use. `advanced`: palette-hidden unless the
  // editor is in advanced mode — for a node whose only sensible home is inside a container.
  if (node.container !== undefined && typeof node.container !== 'boolean') {
    errors.push(`${where}: "container" must be a boolean`)
  }
  if (node.advanced !== undefined && typeof node.advanced !== 'boolean') {
    errors.push(`${where}: "advanced" must be a boolean`)
  }
  if (node.perFrame === true && !isBodyOrFace) {
    errors.push(`${where}: "perFrame" is only valid for body/face graphs`)
  }
  if (node.sentinelClip !== undefined) {
    if (typeof node.sentinelClip !== 'string') errors.push(`${where}: "sentinelClip" must be a string`)
    else if (!isBodyOrFace) errors.push(`${where}: "sentinelClip" is only valid for body/face graphs`)
  }
  if (node.phase !== undefined) {
    if (!PHASES.includes(node.phase)) {
      errors.push(`${where}: "phase" must be one of ${PHASES.join(', ')}`)
    } else if (node.phase === 'postMixer' && !isBodyOrFace) {
      errors.push(`${where}: phase "postMixer" (deformer) is only valid for body/face graphs`)
    }
  }
  // postMixer runs after mixer.update via the deform lane, not in the pre-mixer
  // tick pass — so "perFrame" (a pre-mixer concept) contradicts it.
  if (node.perFrame === true && node.phase === 'postMixer') {
    errors.push(`${where}: a node can't be both "perFrame" and phase "postMixer"`)
  }

  // pins
  if (!isPlainObject(node.pins)) {
    errors.push(`${where}: missing "pins" object (use empty arrays for unused pin lists)`)
  } else {
    const inHandles = new Set()
    const outHandles = new Set()
    validatePinList(node.pins.execIn, 'execIn', where, 'exec', errors, inHandles)
    validatePinList(node.pins.dataIn, 'dataIn', where, 'data', errors, inHandles)
    validatePinList(node.pins.execOut, 'execOut', where, 'exec', errors, outHandles)
    validatePinList(node.pins.dataOut, 'dataOut', where, 'data', errors, outHandles)

    // Exec chains only run in the event graph. A face/body/material node with exec
    // pins is almost certainly a mistake (they're pull-DAGs / value-emit).
    const hasExec = (Array.isArray(node.pins.execIn) && node.pins.execIn.length > 0) ||
                    (Array.isArray(node.pins.execOut) && node.pins.execOut.length > 0)
    if (hasExec && graph !== 'event') {
      errors.push(`${where}: exec pins are only valid for the event graph`)
    }
  }

  if (node.assets !== undefined && !isPlainObject(node.assets)) {
    errors.push(`${where}: "assets" must be an object of { name: filename }`)
  }
}

/**
 * Validate a parsed plugin.json manifest.
 *
 * @param {object} manifest  the parsed plugin.json
 * @param {string} name      the plugin folder name (manifest.name must match)
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateManifest(manifest, name) {
  const errors = []

  if (!isPlainObject(manifest)) {
    return { ok: false, errors: ['manifest must be a JSON object'] }
  }
  if (typeof manifest.name !== 'string' || manifest.name.length === 0) {
    errors.push('missing string "name"')
  } else if (name !== undefined && manifest.name !== name) {
    errors.push(`"name" ("${manifest.name}") must match the plugin folder ("${name}")`)
  }
  if (typeof manifest.version !== 'string' || manifest.version.length === 0) {
    errors.push('missing string "version"')
  }
  if (manifest.description !== undefined && typeof manifest.description !== 'string') {
    errors.push('"description" must be a string')
  }
  for (const f of ['wasm', 'wasmGlue']) {
    if (manifest[f] !== undefined && typeof manifest[f] !== 'string') {
      errors.push(`"${f}" must be a string`)
    }
  }

  if (!Array.isArray(manifest.nodes) || manifest.nodes.length === 0) {
    errors.push('"nodes" must be a non-empty array')
  } else {
    const seenTypes = new Set()
    manifest.nodes.forEach((node, i) => validateNode(node, `nodes[${i}]`, errors, seenTypes))
  }

  return { ok: errors.length === 0, errors }
}
